import { Box, Group, Table, Tooltip } from "@mantine/core";
import React from "react";
import ThrottledTextInput from "../../../../components/ThrottledTextInput";
import useMonitoringQueue from "./useMonitoringQueue.logic";
import SortIcon from "../../../../components/searchOptions/SortIcon";
import DateSearchFilter from "../../../../components/searchOptions/dateSearchFilter/DateSearchFilter";
import { useSelectDataMonitoringQueue } from "../../../../redux/features/monitoringQueue/monitoringQueueSlice";
import IconFlagFilled from "@tabler/icons-react/dist/esm/icons/IconFlagFilled.mjs";
import classes from "./monitoringQueueTable.module.scss";
import dayjs from "dayjs";
import { TableSkeleton } from "../../../../components/Skeletons/TableSkeleton";
import { resolutionStatuses } from "../../../../utils/vars";
import { AppPagination } from "../../../../components/AppPagination/AppPagination";
import { useTranslation } from "react-i18next";
import UnlockButton from "./UnlockButton";
import AssignToButton from "./AssignToButton";
import StatusPill from "./StatusPill";
import { dateFormat } from "../../../../utils/dateFormat";
import { TableNoResults } from "../../../../components/TableNoResults/TableNoResults";

const MonitoringQueueTable = () => {
  const { t } = useTranslation();
  const data = useSelectDataMonitoringQueue();
  const {
    sort,
    filters,
    size,
    page,
    totalCount,
    isFetchingMQ,
    resolutionTextPermission,
    handlePageChange,
    handleSizeChange,
    toggleSort,
    handleFilterChange,
    onRowClick,
  } = useMonitoringQueue();

  // ── Column definitions ────────────────────────────────────────────────────────
  const COLUMNS = [
    { key: "id", label: t("monitoringQueue.flag"), sortKey: "ID", width: 60 },
    {
      key: "date",
      label: t("monitoringQueue.date"),
      sortKey: "DATE",
      width: null,
    },
    {
      key: "ruleName",
      label: t("monitoringQueue.ruleName"),
      sortKey: "RULE_NAME",
      width: null,
    },
    {
      key: "personNames",
      label: t("monitoringQueue.personNames"),
      sortKey: "PERSON_NAMES",
      width: null,
    },
    {
      key: "contractIdentifier",
      label: t("monitoringQueue.kid"),
      sortKey: "CONTRACT_IDENTIFIER",
      width: null,
    },
    {
      key: "assignee",
      label: t("monitoringQueue.assignee"),
      sortKey: "ASSIGNEE",
      width: null,
    },

    {
      key: "status",
      label: t("monitoringQueue.status"),
      sortKey: "STATUS",
      width: 80,
    },

    ...(resolutionTextPermission
      ? [
          {
            key: "resolution",
            label: t("monitoringQueue.resolution"),
            sortKey: "RESOLUTION",
            width: 90,
          },
        ]
      : []),
  ];

  const rows = data.map((row, i) => (
    <Table.Tr onClick={() => onRowClick(row.id)} key={`${row.id}-${i}`}>
      <Table.Td>{row.id}</Table.Td>
      <Table.Td>{dayjs(row.date).format(dateFormat)}</Table.Td>
      <Table.Td>{row.name}</Table.Td>
      <Tooltip label={row.personNames} disabled={!row.personNames}>
        <Table.Td>{row.personNames}</Table.Td>
      </Tooltip>
      <Table.Td>{row.contractIdentifier}</Table.Td>
      <Table.Td>{row.assignee}</Table.Td>
      <Table.Td>
        <StatusPill status={row.status} />
      </Table.Td>
      {resolutionTextPermission ? (
        <Table.Td>{resolutionStatuses[row.resolution]}</Table.Td>
      ) : null}

      <Table.Td onClick={(e) => e.stopPropagation()}>
        <Group gap="md" wrap="nowrap" justify="flex-end">
          {row.assigneeId ? (
            <UnlockButton flagId={row.id} />
          ) : (
            <AssignToButton flagId={row.id} />
          )}
        </Group>
      </Table.Td>
      <Table.Td onClick={(e) => e.stopPropagation()}>
        {row.existsInFlag ? <IconFlagFilled color="red" /> : null}
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <>
      <Box className="table-panel">
        <Table
          className={classes["monitoring-queue-table"]}
          horizontalSpacing="md"
          highlightOnHover
          stickyHeader
        >
          <Table.Thead>
            {COLUMNS.map((col) => {
              if (col.sortKey === "DATE") {
                return (
                  <Table.Th key={col.key}>
                    <Group>
                      <Group
                        style={{ cursor: "pointer" }}
                        onClick={() => toggleSort("DATE")}
                        wrap="nowrap"
                      >
                        {t("monitoringQueue.date")}
                        <SortIcon col={"DATE"} sort={sort} />
                      </Group>
                      <DateSearchFilter
                        placeholder={t("monitoringQueue.date")}
                        onChange={(dates) => handleFilterChange("DATE", dates)}
                      />
                    </Group>
                  </Table.Th>
                );
              }

              return (
                <Table.Th key={col.key}>
                  <Group>
                    <Group
                      style={{ cursor: "pointer" }}
                      onClick={() => toggleSort(col.sortKey)}
                      gap={4}
                      wrap="nowrap"
                    >
                      {col.label}
                      <SortIcon col={col.sortKey} sort={sort} />
                    </Group>
                    <ThrottledTextInput
                      size="xs"
                      w={col.width ? col.width : "auto"}
                      delay={600}
                      placeholder="Search"
                      value={filters[col.key]}
                      onThrottledChange={(val) =>
                        handleFilterChange(col.key, val)
                      }
                    />
                  </Group>
                </Table.Th>
              );
            })}
            <Table.Th style={{ width: 130 }} />
            <Table.Th style={{ width: 40 }} />
          </Table.Thead>
          {data.length === 0 && !isFetchingMQ ? (
            <Table.Tbody>
              <TableNoResults colSpan={COLUMNS.length + 1} />
            </Table.Tbody>
          ) : (
            <RenderTableWithSkeleton />
          )}
        </Table>
      </Box>
      {totalCount > 0 && (
        <AppPagination
          page={page}
          take={size}
          total={totalCount}
          onPageChange={handlePageChange}
          onTakeChange={handleSizeChange}
        />
      )}
    </>
  );

  function RenderTableWithSkeleton() {
    if (isFetchingMQ) {
      return <TableSkeleton cols={9} length={size} />;
    }

    return <Table.Tbody pos="relative">{rows}</Table.Tbody>;
  }
};

export default MonitoringQueueTable;
